/* eslint-disable react/prop-types */
import { Box, ClickAwayListener, Popper, styled, ToggleButton } from '@mui/material';
import type { GetUserMentionsQuery } from '__generated__/graphql';
import { TargetType } from '__generated__/graphql';
import BoldIcon from 'components/Icon/BoldIcon';
import ItalicIcon from 'components/Icon/ItalicIcon';
import LinkIcon from 'components/Icon/LinkIcon';
import OrderedListIcon from 'components/Icon/OrderedListIcon';
import UnderlineIcon from 'components/Icon/UnderlineIcon';
import UnlinkIcon from 'components/Icon/UnlinkIcon';
import UnorderedListIcon from 'components/Icon/UnorderedListIcon';
import UserListPicker from 'components/UserTagging/UserListPicker';
import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  Editor,
  EditorState,
  Modifier,
  RichUtils,
} from 'draft-js';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  buildEditorStateFromHtml,
  buildEditorStateFromText,
  handleRemoveAtSignBeforeCursor,
  handleRichTextWithUserMentionState,
  Mention,
  MentionStyles,
  replaceMentionsWithFallbackContent,
} from 'utils/userTagging';

export interface MentionComponentProps {
  children: React.ReactNode;
  contentState: ContentState;
  entityKey: string;
  darkMode: boolean;
}

export const MentionComponent: React.FC<MentionComponentProps> = ({
  children,
  contentState,
  entityKey,
  darkMode,
}) => {
  const { user: _user } = contentState.getEntity(entityKey).getData();
  return <span style={darkMode ? MentionStyles.darkMode : MentionStyles.default}>{children}</span>;
};

export interface LinkComponentProps {
  children: React.ReactNode;
  contentState: ContentState;
  entityKey: string;
}

export const LinkComponent: React.FC<LinkComponentProps> = ({
  children,
  contentState,
  entityKey,
}) => {
  const { url } = contentState.getEntity(entityKey).getData();
  return (
    <a
      href={url}
      target='_blank'
      rel='noopener noreferrer'
      style={{
        color: '#0066cc',
        textDecoration: 'underline',
        cursor: 'pointer',
        userSelect: 'all',
      }}
    >
      {children}
    </a>
  );
};

export const findMentionEntities = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === 'MENTION';
  }, callback);
};

export const findLinkEntities = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
  }, callback);
};

const StyledToggleButton = styled(
  // eslint-disable-next-line react/display-name
  React.forwardRef<HTMLButtonElement, React.ComponentPropsWithoutRef<typeof ToggleButton>>(
    (props, ref) => <ToggleButton ref={ref} {...props} />
  )
)(({ theme: _theme }) => ({
  border: 'none',
  color: '#000000',
  fontWeight: 'normal',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '30px',
  '&.Mui-disabled': {
    border: 'none',
  },
  '& .MuiSvgIcon-root': {
    fontWeight: 'normal',
    width: '100%',
    height: '100%',
  },
}));

interface RichTextWithUserMentionProps {
  disabled?: boolean;
  initialValue: string;
  // Used to force editor's re-initialization when a user removes a textfield (isMultiple)
  forceValueUpdate: { dt: number; mentions: Mention[] } | undefined;
  users: NonNullable<GetUserMentionsQuery['userMentions']>;
  taggedUsers: Mention[];
  onChange: (text: string, mentions: Mention[]) => void;
  resetEditor?: (fn: () => void) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  enableFormatting?: boolean;
  targetType: TargetType;
  testIds?: {
    editor?: string;
    h1Button?: string;
    h2Button?: string;
    h3Button?: string;
    boldButton?: string;
    italicButton?: string;
    underlineButton?: string;
    bulletListButton?: string;
    numberedListButton?: string;
    linkButton?: string;
    unlinkButton?: string;
    tagButton?: string;
  };
}

export type RichTextWithUserMentionHandle = {
  focusEditor: () => void;
};

export const RichTextWithUserMention = React.memo(
  forwardRef<RichTextWithUserMentionHandle, RichTextWithUserMentionProps>(
    (
      {
        initialValue,
        forceValueUpdate,
        users,
        disabled = false,
        onChange,
        taggedUsers,
        resetEditor,
        onFocus = () => {},
        onBlur = () => {},
        enableFormatting = false,
        targetType,
        testIds,
      },
      ref
    ) => {
      const decorator = useMemo(
        () =>
          new CompositeDecorator([
            {
              strategy: findMentionEntities,
              component: (props: MentionComponentProps) => <MentionComponent {...props} />,
            },
            {
              strategy: findLinkEntities,
              component: (props: LinkComponentProps) => <LinkComponent {...props} />,
            },
          ]),
        []
      );

      const handleResetEditor = useCallback(() => {
        const emptyContentState = ContentState.createFromText('');
        const newEditorState = EditorState.push(editorState, emptyContentState, 'remove-range');
        setEditorState(newEditorState);
      }, []);

      const [searchQuery, setSearchQuery] = useState('');
      const [editorState, setEditorState] = useState(EditorState.createEmpty(decorator));
      const editorRef = useRef<Editor>(null);
      const [isPopperOpen, setPopperOpen] = useState(false);
      const [initilialized, setInitilialized] = useState(false);
      const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
      const mentionButtonRef = useRef<HTMLButtonElement | null>(null);
      const editorToolbarRef = useRef<HTMLDivElement | null>(null);
      const editorWrapperRef = useRef<HTMLDivElement | null>(null);
      const [cursorPosition, setCursorPosition] = useState<{ top: number; left: number } | null>(
        null
      );

      // Reset anchorEl when toolbar becomes enabled
      useEffect(() => {
        if (!disabled && editorToolbarRef.current) {
          setAnchorEl(editorToolbarRef.current);
        }
      }, [disabled]);

      const filteredUsers = useMemo(() => {
        return users.filter((user) => user.name.toLowerCase().includes(searchQuery.toLowerCase()));
      }, [searchQuery, users]);

      useEffect(() => {
        if (resetEditor) {
          resetEditor(handleResetEditor);
        }
      }, []);

      const focusEditor = () => {
        editorRef.current?.focus();
      };

      useImperativeHandle(ref, () => ({ focusEditor }));

      // This ensures that Popper always has a valid anchorEl
      useEffect(() => {
        if (editorToolbarRef.current && !anchorEl) {
          setAnchorEl(editorToolbarRef.current);
        }
      }, [anchorEl]);

      const handleMentionButtonClick = (_event: React.MouseEvent<HTMLElement>) => {
        // TODO refactor code below
        // It's a Duplicate of CommentInput::handleDisplayUserTaggingPicker
        // But not merged in develop branch yet
        // Focus the editor first
        editorRef.current?.focus();

        // Small delay to ensure focus is set before inserting text
        setTimeout(() => {
          const contentState = editorState.getCurrentContent();
          const selectionState = editorState.getSelection();

          // Get the current block and its text
          const blockKey = selectionState.getStartKey();
          const block = contentState.getBlockForKey(blockKey);
          const blockText = block.getText();

          // Get the cursor position
          const cursorOffset = selectionState.getStartOffset();

          // Determine if a space needs to be added
          const needsSpace = cursorOffset > 0 && blockText[cursorOffset - 1] !== ' ';

          // Add a space if necessary, followed by the @ symbol
          const textToInsert = needsSpace ? ' @' : '@';

          // Insert the text at the current selection
          const contentStateWithSymbol = Modifier.insertText(
            contentState,
            selectionState,
            textToInsert
          );

          // Update the editor state with the new content state
          const newEditorState = EditorState.push(
            editorState,
            contentStateWithSymbol,
            'insert-characters'
          );

          const updatedEditorState = EditorState.forceSelection(
            newEditorState,
            contentStateWithSymbol.getSelectionAfter()
          );

          setPopperOpen(true);

          // Set focus back to the editor and update state
          setEditorState(updatedEditorState);

          // Manually call handleEditorStateChange with the updated state
          handleEditorStateChange(updatedEditorState);
        }, 0);
      };

      const handlePopperClose = (event: MouseEvent | TouchEvent) => {
        if (
          anchorEl &&
          (anchorEl.contains(event.target as Node) ||
            editorToolbarRef.current?.contains(event.target as Node) ||
            editorWrapperRef.current?.contains(event.target as Node) ||
            mentionButtonRef.current?.contains(event.target as Node))
        ) {
          return; // Ignore clicks on the button itself, toolbar and editor
        }

        const response = handleRemoveAtSignBeforeCursor(editorState, searchQuery);
        if (response !== undefined) {
          const { state, content } = response;
          setEditorState(EditorState.forceSelection(state, content.getSelectionAfter()));
        }

        setPopperOpen(false);
      };

      const initializeEditor = (mentionList: Mention[]) => {
        let editorStateWithDecorator;

        if (enableFormatting) {
          if (initialValue.trim().length) {
            const contentState = buildEditorStateFromHtml(initialValue, mentionList);
            editorStateWithDecorator = EditorState.createWithContent(contentState, decorator);
          } else {
            editorStateWithDecorator = EditorState.createEmpty(decorator);
          }
        } else {
          editorStateWithDecorator = EditorState.createWithContent(
            buildEditorStateFromText(initialValue, mentionList).getCurrentContent(),
            decorator
          );
        }
        setEditorState(editorStateWithDecorator);
      };

      useEffect(() => {
        if (taggedUsers === undefined) return;

        if (!initilialized && initialValue !== undefined) {
          initializeEditor(taggedUsers);
          setInitilialized(true);
        }
      }, [taggedUsers]);

      useEffect(() => {
        const contentState = editorState.getCurrentContent();
        const { text, mentions } = replaceMentionsWithFallbackContent(
          contentState,
          enableFormatting,
          taggedUsers
        );
        if (initilialized) onChange(text, mentions);
      }, [editorState]);

      useEffect(() => {
        if (forceValueUpdate !== undefined) {
          initializeEditor(forceValueUpdate.mentions);
        }
      }, [forceValueUpdate]);

      const handleEditorStateChange = (state: EditorState) => {
        setEditorState(state);

        const contentState = state.getCurrentContent();
        const selection = state.getSelection();
        const block = contentState.getBlockForKey(selection.getStartKey());
        const blockText = block.getText();

        const cursorPosition = selection.getStartOffset();
        const textBeforeCursor = blockText.substring(0, cursorPosition);
        const atIndex = textBeforeCursor.lastIndexOf('@');

        if (atIndex !== -1) {
          const charBeforeAt = textBeforeCursor[atIndex - 1];
          const isValidAtSymbol = !charBeforeAt || !/[a-zA-Z0-9_]/.test(charBeforeAt);

          if (isValidAtSymbol) {
            const query = textBeforeCursor.slice(atIndex + 1);
            setSearchQuery(query);

            // set cursor position
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const rect = range.getBoundingClientRect();

              setCursorPosition({ top: rect.top, left: rect.left });
            }

            setPopperOpen(true);
          } else {
            setPopperOpen(false);
            setSearchQuery('');
          }
        } else {
          setPopperOpen(false);
          setSearchQuery('');
        }
      };

      const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleMentionButtonClick(e as unknown as React.MouseEvent<HTMLDivElement>);
        }
      }, []);

      const _handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.currentTarget.style.backgroundColor = '#dddce5';
      }, []);

      const _handleMouseLeave = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.currentTarget.style.backgroundColor = '#fafafa';
      }, []);

      const isTextSelected = useMemo(
        () => !editorState.getSelection().isCollapsed(),
        [editorState]
      );

      const handleUserClick = useCallback(
        (user: Mention) => {
          const updatedEditorState: EditorState = handleRichTextWithUserMentionState(
            user,
            editorState,
            searchQuery
          );

          setEditorState(updatedEditorState);
          setPopperOpen(false);
          setSearchQuery('');

          setTimeout(() => {
            editorRef.current?.focus();
          }, 0);
        },
        [editorState, searchQuery]
      );

      const UserListPopper = useMemo(
        () => (
          <UserListPicker
            useAbsolutePositioning={false}
            users={filteredUsers}
            onUserClick={handleUserClick}
            darkMode={false}
            targetType={targetType}
          />
        ),
        [filteredUsers, handleUserClick]
      );

      // Handle inline style
      const toggleInlineStyle = (style: string) => {
        setEditorState(RichUtils.toggleInlineStyle(editorState, style));
      };

      // Handle block style
      const toggleBlockType = (blockType: string) => {
        setEditorState(RichUtils.toggleBlockType(editorState, blockType));
      };

      const isValidUrl = (url: string): boolean => {
        // Basic URL pattern that requires:
        // - Protocol (http/https)
        // - Domain name
        // - TLD (Top Level Domain)
        const urlPattern =
          /^https?:\/\/([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
        return urlPattern.test(url);
      };

      const formatUrl = (url: string): string => {
        url = url.trim();

        // If it doesn't start with http:// or https://, add https://
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          // If it doesn't have a dot, it's probably not a valid domain
          if (!url.includes('.')) {
            return url;
          }
          return `https://${url}`;
        }
        return url;
      };

      // Handle link
      const handleAddLink = () => {
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const block = contentState.getBlockForKey(selection.getStartKey());
        const entityKey = block.getEntityAt(selection.getStartOffset());

        let linkSelection = selection;

        // If cursor is inside a link, get the entire link range
        if (entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK') {
          const blockKey = selection.getStartKey();
          const block = contentState.getBlockForKey(blockKey);
          let start = 0;
          let end = block.getLength();

          // Find the start and end of the link
          for (let i = 0; i < block.getLength(); i++) {
            const currentEntityKey = block.getEntityAt(i);
            if (currentEntityKey === entityKey) {
              start = i;
              while (i < block.getLength() && block.getEntityAt(i) === entityKey) {
                i++;
              }
              end = i;
              break;
            }
          }

          linkSelection = selection.merge({
            anchorKey: blockKey,
            anchorOffset: start,
            focusKey: blockKey,
            focusOffset: end,
          });
        }

        if (!linkSelection.isCollapsed()) {
          // If there's already a link at the selection, show the current URL
          let currentUrl = '';
          if (entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK') {
            currentUrl = contentState.getEntity(entityKey).getData().url;
          }

          let url = window.prompt('Enter URL:', currentUrl);
          if (url === null) {
            // User clicked cancel, do nothing
            return;
          }

          if (!url) {
            // If URL is empty and there was a link, remove it
            if (entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK') {
              const newContentState = Modifier.applyEntity(contentState, linkSelection, null);
              const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
              setEditorState(newEditorState);
            }
            return;
          }

          // Format and validate URL
          url = formatUrl(url);
          if (!isValidUrl(url)) {
            window.alert('Please enter a valid URL');
            return;
          }

          // Create a new entity for the link
          const contentStateWithEntity = contentState.createEntity('LINK', 'IMMUTABLE', { url });
          const newEntityKey = contentStateWithEntity.getLastCreatedEntityKey();

          // Apply the entity to the entire selection
          const newContentState = Modifier.applyEntity(
            contentStateWithEntity,
            linkSelection,
            newEntityKey
          );

          // Create a new editor state with the updated content
          const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');

          // Force selection to be at the end of the link
          const finalEditorState = EditorState.forceSelection(
            newEditorState,
            newContentState.getSelectionAfter()
          );

          setEditorState(finalEditorState);
        }
      };

      // Add this function to check if cursor is inside a link
      const isCursorInsideLink = useCallback(() => {
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const block = contentState.getBlockForKey(selection.getStartKey());
        const entityKey = block.getEntityAt(selection.getStartOffset());
        return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
      }, [editorState]);

      // Add this function to handle unlinking
      const handleUnlink = useCallback(() => {
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const block = contentState.getBlockForKey(selection.getStartKey());
        const entityKey = block.getEntityAt(selection.getStartOffset());

        if (entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK') {
          const blockKey = selection.getStartKey();
          const block = contentState.getBlockForKey(blockKey);
          let start = 0;
          let end = block.getLength();

          // Find the start and end of the link
          for (let i = 0; i < block.getLength(); i++) {
            const currentEntityKey = block.getEntityAt(i);
            if (currentEntityKey === entityKey) {
              start = i;
              while (i < block.getLength() && block.getEntityAt(i) === entityKey) {
                i++;
              }
              end = i;
              break;
            }
          }

          const linkSelection = selection.merge({
            anchorKey: blockKey,
            anchorOffset: start,
            focusKey: blockKey,
            focusOffset: end,
          });

          // Remove the entity from the selection
          const newContentState = Modifier.applyEntity(contentState, linkSelection, null);
          const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
          setEditorState(newEditorState);
        }
      }, [editorState]);

      const handleBeforeInput = useCallback(
        (chars: string, editorState: EditorState) => {
          if (chars === '. ') {
            const currentSelection = editorState.getSelection();
            const newEditorState = EditorState.set(editorState, {
              currentContent: Modifier.replaceText(
                editorState.getCurrentContent(),
                currentSelection,
                ' '
              ),
            });
            setEditorState(newEditorState);
            return 'handled';
          }
          return 'not-handled';
        },
        [editorState]
      );

      return (
        <div className=''>
          {!disabled && (
            <Box
              ref={editorToolbarRef}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                display: 'flex',
                textAlign: 'left',
                border: '1px solid #CECECE',
                borderRadius: '8px',
                alignItems: 'center',
                justifyContent: 'flex-start',
                borderBottom: '0.0625rem solid rgb(202, 202, 202)',
                borderTop: 'none',
                borderLeft: 'none',
                borderRight: 'none',
                backgroundColor: 'rgb(250, 250, 250)',
                borderTopLeftRadius: '0.625rem',
                borderTopRightRadius: '0.625rem',
                padding: '0px 0.875rem',
                height: '25px',
                position: 'sticky',
                top: 0,
                zIndex: 2,
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
                '&::-webkit-scrollbar': {
                  height: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {enableFormatting && (
                <>
                  <StyledToggleButton
                    sx={{ border: 'none' }}
                    onClick={() => toggleBlockType('header-one')}
                    value='H1'
                    data-testid={testIds?.h1Button}
                  >
                    H1
                  </StyledToggleButton>
                  <StyledToggleButton
                    onClick={() => toggleBlockType('header-two')}
                    value='H2'
                    data-testid={testIds?.h2Button}
                  >
                    H2
                  </StyledToggleButton>
                  <StyledToggleButton
                    onClick={() => toggleBlockType('header-three')}
                    value='H3'
                    data-testid={testIds?.h3Button}
                  >
                    H3
                  </StyledToggleButton>

                  <StyledToggleButton
                    onClick={() => toggleInlineStyle('BOLD')}
                    value='BOLD'
                    data-testid={testIds?.boldButton}
                  >
                    <BoldIcon />
                  </StyledToggleButton>
                  <StyledToggleButton
                    onClick={() => toggleInlineStyle('ITALIC')}
                    value='ITALIC'
                    data-testid={testIds?.italicButton}
                  >
                    <ItalicIcon />
                  </StyledToggleButton>
                  <StyledToggleButton
                    onClick={() => toggleInlineStyle('UNDERLINE')}
                    value='UNDERLINE'
                    data-testid={testIds?.underlineButton}
                  >
                    <UnderlineIcon />
                  </StyledToggleButton>

                  <StyledToggleButton
                    onClick={() => toggleBlockType('unordered-list-item')}
                    value='unordered-list-item'
                    data-testid={testIds?.bulletListButton}
                  >
                    <UnorderedListIcon />
                  </StyledToggleButton>
                  <StyledToggleButton
                    onClick={() => toggleBlockType('ordered-list-item')}
                    value='ordered-list-item'
                    data-testid={testIds?.numberedListButton}
                  >
                    <OrderedListIcon />
                  </StyledToggleButton>

                  <StyledToggleButton
                    onClick={handleAddLink}
                    value='LINK'
                    data-testid={testIds?.linkButton}
                  >
                    <LinkIcon />
                  </StyledToggleButton>

                  <StyledToggleButton
                    onClick={handleUnlink}
                    value='UNLINK'
                    disabled={!isCursorInsideLink()}
                    sx={{
                      opacity: isCursorInsideLink() ? 1 : 0.5,
                      cursor: isCursorInsideLink() ? 'pointer' : 'not-allowed',
                      border: 'none',
                    }}
                    data-testid={testIds?.unlinkButton}
                  >
                    <UnlinkIcon />
                  </StyledToggleButton>
                </>
              )}

              <StyledToggleButton
                ref={mentionButtonRef}
                onClick={(event) => {
                  if (isTextSelected || isCursorInsideLink()) {
                    event.preventDefault();
                    return;
                  }

                  handleMentionButtonClick(event);
                }}
                style={{
                  cursor: isTextSelected || isCursorInsideLink() ? 'not-allowed' : 'pointer',
                  opacity: isTextSelected || isCursorInsideLink() ? 0.5 : 1,
                }}
                onKeyDown={handleKeyPress}
                onMouseDown={(event) => event.preventDefault()} // Prevents focus shift
                value='ordered-list-item'
                data-testid={testIds?.tagButton}
              >
                @
              </StyledToggleButton>
            </Box>
          )}

          <Box
            ref={editorWrapperRef}
            sx={{
              lineHeight: 1.6,
              overflowY: 'auto',
              padding: '0px 0.875rem',
            }}
            data-testid={testIds?.editor}
          >
            <Editor
              ref={editorRef}
              editorState={editorState}
              onChange={handleEditorStateChange}
              placeholder={targetType === 'TemplateResponse' ? 'Answer here' : ''}
              readOnly={disabled}
              onBlur={onBlur}
              onFocus={onFocus}
              spellCheck={true}
              handleBeforeInput={handleBeforeInput}
            />
          </Box>

          <ClickAwayListener onClickAway={handlePopperClose}>
            <Popper
              open={isPopperOpen && filteredUsers.length > 0}
              anchorEl={anchorEl}
              placement={cursorPosition ? 'top-start' : 'bottom-start'}
              sx={{
                zIndex: 11,
              }}
              style={
                cursorPosition
                  ? {
                      position: 'absolute',
                      top: cursorPosition.top + window.scrollY,
                      left: cursorPosition.left,
                    }
                  : undefined
              }
            >
              {UserListPopper}
            </Popper>
          </ClickAwayListener>
        </div>
      );
    }
  )
);

RichTextWithUserMention.displayName = 'RichTextWithUserMention';
